import type {
  AccountPreferences,
  RidersClubTC,
} from "@/apis/schemas/account-info";
import { getServerConfig } from "@/lib/server/actions/server-config";
import type {
  EmailSubscription,
  Subscriptions,
} from "@/types/bike-registration";

const PRIVACY_POLICY_DOC_VERSION =
  process.env.NEXT_PUBLIC_PRIVACY_POLICY_DOC_VERSION;
const RIDER_CLUB_TC_DOC_VERSION =
  process.env.NEXT_PUBLIC_RIDER_CLUB_TC_DOC_VERSION;

export function buildPreferences(joinRidersClub?: boolean): AccountPreferences {
  const brand = getServerConfig().brand;
  if (!brand?.value) {
    throw new Error("Brand must be defined with a valid value");
  }
  const brandValue = brand.value.charAt(0).toUpperCase() + brand.value.slice(1);

  const base: AccountPreferences = {
    privacy: {
      [`${brandValue}_PrivacyPolicy`]: {
        actionTimestamp: new Date().toISOString(),
        docVersion: Number(PRIVACY_POLICY_DOC_VERSION),
        isConsentGranted: true,
      },
    },
  };

  if (joinRidersClub) {
    base.Rider_Club_TC = {
      isConsentGranted: true,
      docVersion: Number(RIDER_CLUB_TC_DOC_VERSION),
      lang: "en",
      lastConsentModified: new Date().toISOString(),
      actionTimestamp: new Date().toISOString(),
      tags: [],
      customData: [],
      entitlements: [],
    } as RidersClubTC;
  }

  return base;
}

export function buildSubscriptions(
  categoryNames: string[],
  country: string
): Subscriptions {
  const brand = getServerConfig().brand;
  if (!brand?.value) {
    throw new Error("Brand must be defined with a valid value");
  }
  const brandValue = brand.value.charAt(0).toUpperCase() + brand.value.slice(1);
  const isUSCountry = country?.toLowerCase() === "us";
  const optInKey = isUSCountry
    ? `US_${brandValue}_Optin`
    : `${brandValue}_Optin`;

  const subscriptions: Subscriptions = {
    [optInKey]: {
      email: {
        isSubscribed: true,
        doubleOptIn: {
          status: isUSCountry ? "Confirmed" : "Pending",
        },
      },
    },
  };

  const subscriptionTemplate: EmailSubscription = {
    isSubscribed: true,
    doubleOptIn: {
      status: "NotConfirmed",
    },
  };

  categoryNames.forEach((category) => {
    subscriptions[category] = { email: { ...subscriptionTemplate } };
  });

  return subscriptions;
}

export const mapCategoryCodeToCategoryName = (categoryCode: string) => {
  if (!categoryCode) {
    return "";
  }
  return (
    mapMerchandiseCategoriesCodeToCategoryName[categoryCode] ?? categoryCode
  );
};

const mapMerchandiseCategoriesCodeToCategoryName: { [key: string]: string } = {
  S010101: "MOUNTAIN",
  S010102: "ROAD",
  S010103: "KIDS",
  S010104: "JUNIOR",
  S010106: "URBAN/TREKKING",
  S010107: "E-MOUNTAIN",
  S010108: "E-ROAD",
  S010109: "E-URBAN/TREKKING",
  S010110: "E-KIDS",
};
